import React, { useState, useEffect } from 'react';
import '../styles/PermanenceNumerique.css';
import { createReservation, getAllPermanences } from '../services/api';
import OrganismesPartenaires from '../components/OrganismesPartenaires';

function PermanenceNumerique() {
  const [permanences, setPermanences] = useState([]);
  const [placesRestantes, setPlacesRestantes] = useState({});
  const [selectedPermanence, setSelectedPermanence] = useState(null);
  const [creneauxDisponibles, setCreneauxDisponibles] = useState([]);
  const [formData, setFormData] = useState({
    permanence: '',
    creneauHoraire: '',
    concernePour: 'moi-meme',
    nom: '',
    prenom: '',
    telephone: '',
    adresse: '',
    email: '',
    besoin: '',
    commentaire: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPermanences();
  }, []);

  const fetchPermanences = async () => {
    try {
      const response = await getAllPermanences();
      setPermanences(response.data);
      
      // Charger les places restantes pour chaque permanence
      const { getDisponibilites } = await import('../services/api');
      const placesData = {};
      
      for (const perm of response.data) {
        try {
          const dispResponse = await getDisponibilites(perm._id);
          const { disponibilites } = dispResponse.data;
          
          // Calculer le total de places restantes
          const totalPlacesRestantes = Object.values(disponibilites).reduce(
            (sum, creneau) => sum + creneau.disponibles, 
            0
          );
          
          placesData[perm._id] = totalPlacesRestantes;
        } catch (error) {
          console.error(`Erreur chargement places pour ${perm._id}:`, error);
          placesData[perm._id] = perm.capaciteMax;
        }
      }
      
      setPlacesRestantes(placesData);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const generateCreneaux = (heureDebut, heureFin) => {
    const creneaux = [];
    const [hDebut, mDebut] = heureDebut.split(':').map(Number);
    const [hFin, mFin] = heureFin.split(':').map(Number);
    
    let currentHeure = hDebut;
    let currentMinute = mDebut;
    
    while (currentHeure < hFin || (currentHeure === hFin && currentMinute < mFin)) {
      const formatted = `${String(currentHeure).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;
      creneaux.push(formatted);
      
      currentMinute += 30;
      if (currentMinute >= 60) {
        currentMinute = 0;
        currentHeure += 1;
      }
    }
    
    return creneaux;
  };

  const handleInscription = async (permanenceId, permanenceTitre, heureDebut, heureFin) => {
    // Vérifier si la permanence est complète
    if (placesRestantes[permanenceId] === 0) {
      alert('Désolé, cette permanence est complète.');
      return;
    }
    
    setSelectedPermanence(permanenceTitre);
    setFormData({ ...formData, permanence: permanenceId, creneauHoraire: '' });
    
    // Charger les disponibilités
    try {
      const { getDisponibilites } = await import('../services/api');
      const response = await getDisponibilites(permanenceId);
      const { creneaux, disponibilites } = response.data;
      
      // Filtrer les créneaux complets
      const creneauxDispos = creneaux.filter(c => !disponibilites[c].complet);
      
      if (creneauxDispos.length === 0) {
        alert('Tous les créneaux sont complets pour cette permanence.');
        return;
      }
      
      setCreneauxDisponibles(creneauxDispos.map(c => ({
        value: c,
        label: `${c} (${disponibilites[c].disponibles} places restantes)`
      })));
    } catch (error) {
      console.error('Erreur chargement disponibilités:', error);
      const creneaux = generateCreneaux(heureDebut, heureFin);
      setCreneauxDisponibles(creneaux.map(c => ({ value: c, label: c })));
    }
    
    document.getElementById('formulaire-inscription').scrollIntoView({ behavior: 'smooth' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await createReservation(formData);

      if (response.success) {
        setSubmitted(true);
        console.log('✅ Demande créée avec succès:', response.data);
        
        if (response.emailSent) {
          console.log('📧 Email de confirmation envoyé');
        }

        setTimeout(() => {
          setSubmitted(false);
          setSelectedPermanence(null);
          setCreneauxDisponibles([]);
          setFormData({
            permanence: '',
            creneauHoraire: '',
            concernePour: 'moi-meme',
            nom: '',
            prenom: '',
            telephone: '',
            adresse: '',
            email: '',
            besoin: '',
            commentaire: ''
          });
        }, 5000);
      }
    } catch (error) {
      console.error('❌ Erreur lors de la demande:', error);
      
      if (error.response) {
        alert(`Erreur: ${error.response.data.message || 'Une erreur est survenue'}`);
      } else if (error.request) {
        alert('Impossible de contacter le serveur. Veuillez réessayer plus tard.');
      } else {
        alert('Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="permanence-container">
      {/* Hero Section */}
      <section className="permanence-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>Permanence Numérique</h1>
          <p>Accompagnement aux démarches administratives en ligne</p>
        </div>
      </section>

      {/* Section Présentation */}
      <section className="presentation-section">
        <div className="container">
          <div className="presentation-content">
            <h2>Notre Mission</h2>
            <p className="lead-text">
              Notre association propose une permanence d'accès aux droits et un accompagnement 
              numérique afin de soutenir les personnes dans leurs démarches administratives en ligne.
            </p>
            <p className="description-text">
              Nous offrons un espace d'écoute, d'orientation et d'aide pratique pour favoriser 
              l'autonomie numérique et garantir l'accès aux services essentiels pour toutes et tous.
            </p>
          </div>
        </div>
      </section>

      {/* Section Permanences à venir */}
      {permanences.length > 0 && (
        <section className="prochaines-permanences-section">
          <div className="container">
            <h2>📅 Prochaines Permanences</h2>
            <div className="permanences-grid">
              {permanences.map((p) => (
                <div key={p._id} className="permanence-card-public">
                  <div className="permanence-date">
                    <div className="date-jour">{new Date(p.date).getDate()}</div>
                    <div className="date-mois">
                      {new Date(p.date).toLocaleDateString('fr-FR', { month: 'short' })}
                    </div>
                  </div>
                  <div className="permanence-details">
                    <h3>{p.titre}</h3>
                    <p>{p.description}</p>
                    <div className="permanence-info">
                      <div>🕒 {p.heureDebut} - {p.heureFin}</div>
                      <div>📍 {p.lieu.ville} - {p.lieu.adresse}</div>
                      {p.lieu.salle && <div>Salle Numéro - {p.lieu.salle}</div>}
                    </div>
                    <div className="services-tags">
                      {p.services.map((s, i) => (
                        <span key={i} className="service-tag">
                          {s === 'titre-sejour-premiere' && '🛂 Première demande titre séjour'}
                          {s === 'titre-sejour-renouvellement' && '🔄 Renouvellement titre séjour'}
                          {s === 'nationalite-francaise' && '🇫🇷 Nationalité française'}
                          {s === 'changement-statut' && '🎓 Changement de statut'}
                          {s === 'caf-emploi-retraite' && '📝 CAF/France Travail/Retraite'}
                          {s === 'etudiant' && '🎓 Etudiant'}
                        </span>
                      ))}
                    </div>
                    <button 
                      onClick={() => handleInscription(p._id, p.titre, p.heureDebut, p.heureFin)}
                      className="btn-inscrire"
                      disabled={placesRestantes[p._id] === 0}
                    >
                      {placesRestantes[p._id] === 0 
                        ? '❌ Complet' 
                        : `✍️ S'inscrire à cette permanence (${placesRestantes[p._id] || '...'} place${placesRestantes[p._id] > 1 ? 's' : ''} restante${placesRestantes[p._id] > 1 ? 's' : ''})`
                      }
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Section À qui s'adresse */}
      <section className="public-section">
        <div className="container">
          <h2>À qui s'adresse cette permanence ?</h2>
          <div className="public-grid">
            <div className="public-card">
              <div className="public-icon">👥</div>
              <h3>Tous publics</h3>
              <p>Toute personne ayant besoin d'aide pour ses démarches en ligne</p>
            </div>
            <div className="public-card">
              <div className="public-icon">👴</div>
              <h3>Seniors</h3>
              <p>Accompagnement personnalisé pour les personnes âgées</p>
            </div>
            <div className="public-card">
              <div className="public-icon">🌍</div>
              <h3>Personnes éloignées du numérique</h3>
              <p>Soutien pour ceux qui ne maîtrisent pas les outils numériques</p>
            </div>
            <div className="public-card">
              <div className="public-icon">📄</div>
              <h3>Démarches administratives</h3>
              <p>Aide pour titres de séjour, CAF, Pôle Emploi, etc.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section Services */}
      <section className="services-section">
        <div className="container">
          <h2>Nos Services</h2>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">🛂</div>
              <h3>Titres de séjour</h3>
              <p>Première demande ou renouvellement de titre de séjour</p>
            </div>
            <div className="service-card">
              <div className="service-icon">🇫🇷</div>
              <h3>Nationalité française</h3>
              <p>Demande d'accès à la nationalité française</p>
            </div>
            <div className="service-card">
              <div className="service-icon">🎓</div>
              <h3>Changement de statut</h3>
              <p>Passage du statut étudiant à salarié</p>
            </div>
            <div className="service-card">
              <div className="service-icon">📝</div>
              <h3>Démarches sociales</h3>
              <p>CAF, France Travail, retraite, sécurité sociale</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section Organismes Partenaires */}
      <OrganismesPartenaires />

      {/* Section Formulaire */}
      {permanences.length > 0 && Object.values(placesRestantes).some(places => places > 0) && selectedPermanence && (
        <section id="formulaire-inscription" className="reservation-section">
          <div className="container">
            <div className="reservation-wrapper">
              <div className="reservation-header">
                <h2>📝 Faire une demande d'accompagnement</h2>
                <p>Remplissez ce formulaire et nous vous recontacterons rapidement</p>
                {selectedPermanence && (
                  <div className="permanence-selectionnee">
                    ✅ Inscription pour : <strong>{selectedPermanence}</strong>
                  </div>
                )}
              </div>

          

            <form onSubmit={handleSubmit} className="reservation-form">
              {selectedPermanence && creneauxDisponibles.length > 0 && (
                <div className="form-section">
                  <h3>⏰ Choisissez votre créneau horaire</h3>
                  <div className="form-group">
                    <label htmlFor="creneauHoraire">Créneau *</label>
                    <select
                      id="creneauHoraire"
                      name="creneauHoraire"
                      value={formData.creneauHoraire}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Sélectionnez un créneau</option>
                      {creneauxDisponibles.map((creneau) => (
                        <option key={creneau.value || creneau} value={creneau.value || creneau}>
                          {creneau.label || creneau}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              
              <div className="form-section">
                <h3>La demande concerne</h3>
                <div className="form-group">
                  <label htmlFor="concernePour">Cette démarche concerne *</label>
                  <select
                    id="concernePour"
                    name="concernePour"
                    value={formData.concernePour}
                    onChange={handleChange}
                    required
                  >
                    <option value="moi-meme">Moi-même</option>
                    <option value="quelqu-un-autre">Quelqu'un d'autre</option>
                  </select>
                </div>
              </div>

              <div className="form-section">
                <h3>Vos informations</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="nom">Nom *</label>
                    <input
                      type="text"
                      id="nom"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      placeholder="Votre nom"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="prenom">Prénom *</label>
                    <input
                      type="text"
                      id="prenom"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      required
                      placeholder="Votre prénom"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="telephone">Numéro de téléphone *</label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    required
                    placeholder="06 12 34 56 78"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="adresse">Adresse postale *</label>
                  <input
                    type="text"
                    id="adresse"
                    name="adresse"
                    value={formData.adresse}
                    onChange={handleChange}
                    required
                    placeholder="Votre adresse complète"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email (optionnel)</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="votre.email@exemple.com"
                  />
                  <small className="form-help">Si vous fournissez un email, vous recevrez une confirmation</small>
                </div>
              </div>

              <div className="form-section">
                <h3>Votre demande</h3>
                <div className="form-group">
                  <label htmlFor="besoin">Type de démarche *</label>
                  <select
                    id="besoin"
                    name="besoin"
                    value={formData.besoin}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Sélectionnez votre besoin</option>
                    <option value="titre-sejour-premiere">Première demande de titre de séjour</option>
                    <option value="titre-sejour-renouvellement">Renouvellement d'un titre de séjour</option>
                    <option value="nationalite-francaise">Demande d'accès à la nationalité française</option>
                    <option value="changement-statut">Changement de statut étudiant à salarié</option>
                    <option value="caf-emploi-retraite">CAF, France Travail, retraite, sécurité sociale</option>
                     <option value="etudiant">Etudiant</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="commentaire">Commentaire / Décrivez votre situation (optionnel)</label>
                  <textarea
                    id="commentaire"
                    name="commentaire"
                    value={formData.commentaire}
                    onChange={handleChange}
                    rows="5"
                    placeholder="Expliquez brièvement votre situation et ce dont vous avez besoin..."
                  ></textarea>
                </div>
              </div>

                {submitted && (
              <div className="success-message">
                ✓ Votre demande a été envoyée avec succès ! 
                {formData.email && ` Un email de confirmation a été envoyé à ${formData.email}.`}
              </div>
            )}

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Envoi en cours...' : 'Envoyer ma demande'}
              </button>

              <p className="form-note">
                * Champs obligatoires.
              </p>
            </form>
          </div>
        </div>
      </section>
      )}

      {/* Message si tout est complet */}
      {permanences.length > 0 && Object.values(placesRestantes).every(places => places === 0) && (
        <section className="reservation-section">
          <div className="container">
            <div className="reservation-wrapper">
              <div className="complet-message">
                <h2>Toutes les permanences sont complètes</h2>
                <p>Malheureusement, toutes nos permanences actuelles sont complètes. De nouvelles dates seront bientôt disponibles.</p>
                <p>Vous pouvez nous contacter par email à <a href="mailto:asifc.rhone@gmail.com">asifc.rhone@gmail.com</a></p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Section Contact */}
      <section className="contact-permanence-section">
        <div className="container">
          <h2>Informations pratiques</h2>
          <div className="infos-pratiques-grid">
            <div className="info-pratique-card">
              {/* <div className="info-icon">📍</div> */}
              <h3>Adresse</h3>
               <p>
                  13 avenue Marcel Paul<br/> Vénissieux 69200
                </p>
            </div>
            <div className="info-pratique-card">
              {/* <div className="info-icon">📧</div> */}
              <h3>Email</h3>
              <p>asifc.rhone@gmail.com</p>
            </div>
            <div className="info-pratique-card">
              {/* <div className="info-icon">📞</div> */}
              <h3>Téléphone</h3>
              <p>À venir</p>
            </div>
            <div className="info-pratique-card">
              {/* <div className="info-icon">🕒</div> */}
              <h3>Horaires</h3>
              <p>Lundi au Samedi<br/>9h - 18h</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PermanenceNumerique;